import cls from "classnames"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
    getCharaAvatar,
    getSpeakerDecorLabel,
} from "@/components/DialogBox/const"
import Toast from "@/components/Toast"
import { galleryItems } from "@/data/gallery"
import { drawQRCode, useShareChannels } from "@/hooks"
import { type I18nTextPath, i18n } from "@/i18n"
import { useModalStore } from "@/store"
import { useRoleInfo } from "@/user/session"
import { useLottery } from "../Lottery"
import { CustomModal } from "../Modal"
import ShareButton from "../ShareButton"
import StrokeText from "../StrokeText"
import { px2rem } from "@/utils/adaptFont"
import { drawShareImage } from "./helper"

import styles from "./index.module.scss"

const SHARE_DIALOG_TEXT =
    "对话框能字数最多为40个字对话框能字数最多为40个字对话框能字数最多为40个字啊"

// 对话框内容宽度（设计稿空间）
const DIALOG_CONTENT_WIDTH = 279
const FONT_SANS = '"PingFang SC", "Microsoft YaHei", sans-serif'

/**
 * 文本换行处理
 */
const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines = Number.POSITIVE_INFINITY,
): string[] => {
    const paragraphs = text.split("\n")
    const lines: string[] = []

    for (const paragraph of paragraphs) {
        if (!paragraph) {
            lines.push("")
            continue
        }

        let currentLine = ""
        for (const char of Array.from(paragraph)) {
            const nextLine = currentLine + char
            if (
                currentLine &&
                ctx.measureText(nextLine).width > maxWidth &&
                lines.length + 1 < maxLines
            ) {
                lines.push(currentLine)
                currentLine = char
            } else if (
                currentLine &&
                ctx.measureText(nextLine).width > maxWidth
            ) {
                currentLine = nextLine
            } else {
                currentLine = nextLine
            }
        }
        lines.push(currentLine)
    }

    if (lines.length <= maxLines) {
        return lines
    }

    const trimmedLines = lines.slice(0, maxLines)
    let lastLine = trimmedLines[maxLines - 1] || ""

    while (lastLine && ctx.measureText(`${lastLine}...`).width > maxWidth) {
        lastLine = lastLine.slice(0, -1)
    }

    trimmedLines[maxLines - 1] = `${lastLine}...`
    return trimmedLines
}

/**
 * 根据文本内容计算对话框应使用的字号（21 / 17 / 15 px）
 */
const computeDialogFontSize = (
    text: string,
    rootFontSize = 16,
): number => {
    if (typeof document === "undefined") return 21
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) return 21
    ctx.font = `500 ${(21 / 16) * rootFontSize}px ${FONT_SANS}`
    const lines = wrapText(ctx, text, DIALOG_CONTENT_WIDTH)
    return lines.length > 4 ? 15 : lines.length > 3 ? 17 : 21
}

/**
 * 根据字号返回对应的行高（设计稿空间，单位 px）
 */
const computeDialogLineHeight = (fontSize: number): number => {
    switch (fontSize) {
        case 14:
            return 22
        case 17:
            return 27
        default:
            return 33
    }
}

export const ShareModal: React.FC = () => {
    const open = useModalStore((state) => state.shareOpen)
    const onCancel = useModalStore((state) => state.closeShare)
    const galleryId = useModalStore((state) => state.galleryId)
    const eventImage = useModalStore((state) => state.eventImage)
    const storeDialogText = useModalStore((state) => state.dialogText)
    const hideDialog = useModalStore((state) => state.hideDialog)
    const galleryItem = useMemo(() => {
        return galleryItems.find((item) => item.id === galleryId)
    }, [galleryId])

    const speaker = useModalStore((state) => state.speaker)
    const expression = useModalStore((state) => state.expression)

    const shareImage = galleryItem?.image ?? eventImage
    const dialogText = storeDialogText || SHARE_DIALOG_TEXT

    const charAvatar = speaker ? getCharaAvatar(speaker, expression) : undefined
    const displayAppellation = speaker
        ? getSpeakerDecorLabel(speaker)
        : i18n.get("char.char_472_pasngr.appellation")
    const displayName = speaker
        ? i18n.get(speaker as never)
        : i18n.get("char.char_472_pasngr.name")

    const user = useRoleInfo()
    const [qrCodeUrl, setQrCodeUrl] = useState("")
    const [imageSrc, setImageSrc] = useState("")
    const imageRef = useRef<HTMLImageElement>(null)
    const shareTips = i18n.getArray("map.gallery.shareTips")
    const dialogFontSize = useMemo(
        () => computeDialogFontSize(dialogText),
        [dialogText],
    )

    const generateQRCode = useCallback(async () => {
        if (!open) return

        const qrCanvas = await drawQRCode(user?.uid, false)
        setQrCodeUrl(qrCanvas.toDataURL("image/png"))
    }, [user?.uid, open])

    const generateShareImage = useCallback(async () => {
        if (!shareImage || !qrCodeUrl) return

        const { base64 } = await drawShareImage({
            galleryImage: shareImage,
            qrCodeUrl,
            tips: shareTips,
            appellation: displayAppellation,
            name: displayName,
            dialogText,
            hideDialog,
            charImage: charAvatar?.src,
        })
        setImageSrc(base64)
    }, [
        shareImage,
        shareTips,
        displayAppellation,
        displayName,
        qrCodeUrl,
        dialogText,
        hideDialog,
        charAvatar?.src,
    ])

    const download = useCallback(async () => {
        if (!imageSrc) {
            Toast.message("download error")
            return
        }

        try {
            const link = document.createElement("a")
            link.href = imageSrc
            link.download = `${i18n.get("share.title")}.png`
            document.body.append(link)
            link.click()
            link.remove()
        } catch {
            Toast.message("download error")
        }
    }, [imageSrc])

    const lottery = useLottery()
    const hasShowLottery = useRef(false)

    useEffect(() => {
        if (!open) hasShowLottery.current = false
    }, [open])

    const handleFirstMainShare = useCallback(() => {
        if (hasShowLottery.current) return
        hasShowLottery.current = true
        onCancel()
        lottery?.start()
    }, [lottery, onCancel])

    const { channels, onClickChannel } = useShareChannels({
        download,
        isRecall: false,
        imageRef,
        imageSrc,
        onFirstMainShare: handleFirstMainShare,
    })

    useEffect(() => {
        generateQRCode()
    }, [generateQRCode])

    useEffect(() => {
        generateShareImage()
    }, [generateShareImage])

    const handleCancel = useCallback(() => {
        onCancel()
    }, [onCancel])

    return (
        <>
        <CustomModal
            open={open}
            onCancel={handleCancel}
            showCancelBtn={false}
            showOkBtn={false}
            showClose={false}
            maskClosable
            contentClassName={styles.share}
        >
            <div className={styles.main}>
                {shareImage && (
                    <div className={styles.cg}>
                        <img
                            src={shareImage}
                            alt={
                                galleryItem
                                    ? i18n.get(
                                          galleryItem.title as I18nTextPath,
                                      )
                                    : ""
                            }
                        />
                    </div>
                )}
                <div className={styles.tips}>
                    {shareTips.map((text, i) => (
                        <span
                            key={i}
                            className={cls(
                                styles.text,
                                i === 0 && styles.highlight,
                            )}
                        >
                            {text}
                        </span>
                    ))}
                </div>
            </div>
            <div className={styles.side}>
                <div
                    className={styles.charDialog}
                    style={hideDialog ? { visibility: "hidden" } : undefined}
                >
                    {charAvatar && (
                        <img
                            className={styles.char}
                            src={charAvatar.src}
                            alt=""
                            draggable={false}
                        />
                    )}
                    <div className={styles.dialog}>
                        <div className={styles.charName}>
                            <StrokeText
                                text={displayName}
                                fontFamily="Georgia-Bold"
                                fontSize={28}
                                fillColor="#443122"
                                inner={{
                                    color: "#ffffff",
                                    width: 3,
                                }}
                                outer={{
                                    color: "#f6d1aa",
                                    width: 3,
                                }}
                                offsetY={0}
                                padding={6}
                                className={styles.name}
                            />
                        </div>
                        <div
                            className={styles.text}
                            style={
                                dialogFontSize !== 21 || computeDialogLineHeight(dialogFontSize) !== 33
                                    ? {
                                        fontSize: px2rem(dialogFontSize),
                                    } : undefined
                            }
                        >
                            {dialogText}
                        </div>
                    </div>
                </div>
                <div className={styles.qrcode}>
                    <img src={qrCodeUrl} alt="qrcode" />
                </div>
            </div>
            {imageSrc && (
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt={i18n.get("share.title")}
                    decoding="sync"
                    loading="eager"
                    className={styles.shareImage}
                />
            )}
            <div className={styles.shareButtons}>
                {channels.map((key) => (
                    <ShareButton
                        key={key}
                        type={key}
                        onClick={() => onClickChannel(key)}
                    />
                ))}
            </div>
        </CustomModal>
        {lottery?.modal}
        </>
    )
}
